import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { SelectBox } from './SelectBox';
import {
  CandidateDayForBirthDay,
  CandidateMonthForBirthDay,
  CandidateYearForBirthDay,
} from '../services/DateService';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { AppContext } from 'templates/AppContextProvider';

type InputFieldProps = React.InputHTMLAttributes<HTMLInputElement>;

const inputFieldStyles = [
  'border-[--c-textinput-border]',
  'text-[--c-textinput-text]',
  'bg-[--c-textinput-background]',
  'rounded-[8px]',
  'h-[40px]',
  'px-2',
  'border-[1px]',
  'shrink',
  'min-w-[50px]',
  'max-w-full',
];

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  ({ type = 'text', className: propClassName, autoComplete, ...props }, ref) => {
    return (
      <input
        type={type}
        className={inputFieldStyles.join(' ') + ' ' + propClassName}
        autoComplete={autoComplete}
        {...props}
        ref={ref}
      />
    );
  }
);
InputField.displayName = 'InputField';

export const DatePicker: React.FC<{
  value?: Date;
  id?: string;
  onChange: (d: Date) => void;
}> = ({ value: initialValue, id, onChange }) => {
  const { t: tUserEditDelete } = useTranslationWithLang('userEditDelete');
  const [value, setValue] = useState(initialValue);

  const handleYearChange = (year: string) => {
    const newDate = new Date(parseInt(year), value?.getMonth() ?? 0, value?.getDate() ?? 1);
    setValue(newDate);
    onChange(newDate);
  };

  const handleMonthChange = (month: string) => {
    const newDate = new Date(
      value?.getFullYear() ?? new Date().getFullYear(),
      parseInt(month) - 1,
      value?.getDate() ?? 1
    );
    setValue(newDate);
    onChange(newDate);
  };

  const handleDayChange = (day: string) => {
    const newDate = new Date(
      value?.getFullYear() ?? new Date().getFullYear(),
      value?.getMonth() ?? new Date().getMonth(),
      parseInt(day)
    );
    setValue(newDate);
    onChange(newDate);
  };

  return (
    <div className="flex gap-2">
      <SelectBox
        className="w-32 text-[--c-textinput-text] bg-[--c-textinput-background]"
        name="birthday-year"
        placeholder="2000"
        options={CandidateYearForBirthDay()}
        value={value?.getFullYear().toString() ?? ''}
        onChange={handleYearChange}
        ariaLabel={tUserEditDelete('TU006_038')}
        autoComplete="bday-year"
        id={id}
      />
      <SelectBox
        className="w-16 text-[--c-textinput-text] bg-[--c-textinput-background]"
        name="birthday-month"
        placeholder="01"
        options={CandidateMonthForBirthDay()}
        value={(value != null && (value.getMonth() + 1).toString()) || ''}
        onChange={handleMonthChange}
        ariaLabel={tUserEditDelete('TU006_039')}
        autoComplete="bday-month"
        id={id}
      />
      <SelectBox
        className="w-16 text-[--c-textinput-text] bg-[--c-textinput-background]"
        name="birthday-day"
        placeholder="01"
        options={CandidateDayForBirthDay(value?.getFullYear(), (value?.getMonth() ?? 0) + 1)}
        value={value?.getDate().toString() ?? ''}
        onChange={handleDayChange}
        ariaLabel={tUserEditDelete('TU006_040')}
        autoComplete="bday-day"
        id={id}
      />
    </div>
  );
};

export const YearMonthPicker: React.FC<{
  value?: Date | null;
  id?: string;
  onChange: (d: Date) => void;
}> = ({ value: initialValue, id, onChange }) => {
  const { t: tUserEditDelete } = useTranslationWithLang('userEditDelete');
  const { currentLang } = useContext(AppContext);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    if (selectedMonth) {
      const nextMonth = parseInt(selectedMonth);
      const newDate = new Date(parseInt(year), nextMonth, 0);
      onChange(newDate);
    }
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    if (selectedYear) {
      const nextMonth = parseInt(month);
      const newDate = new Date(parseInt(selectedYear), nextMonth, 0);
      onChange(newDate);
    }
  };

  useEffect(() => {
    if (initialValue && initialValue.getTime() !== 0) {
      setSelectedYear(initialValue.getFullYear().toString());
      setSelectedMonth((initialValue.getMonth() + 1).toString());
    } else {
      setSelectedYear('');
      setSelectedMonth('');
    }
  }, [initialValue]);
  return (
    <div className="flex gap-2">
      {currentLang === 'en' ? (
        <>
          <SelectBox
            className="w-26 text-[--c-textinput-text] bg-[--c-textinput-background]"
            name="yearmonth-month"
            placeholder={tUserEditDelete('TU006_039')}
            options={CandidateMonthForBirthDay()}
            value={selectedMonth}
            onChange={handleMonthChange}
            ariaLabel={tUserEditDelete('TU006_039')}
            autoComplete="bday-month"
            id={id}
          />
          <SelectBox
            className="w-32 text-[--c-textinput-text] bg-[--c-textinput-background]"
            name="yearmonth-year"
            placeholder={tUserEditDelete('TU006_038')}
            options={CandidateYearForBirthDay()}
            value={selectedYear}
            onChange={handleYearChange}
            ariaLabel={tUserEditDelete('TU006_038')}
            autoComplete="bday-year"
            id={id}
          />
        </>
      ) : (
        <>
          <SelectBox
            className="w-32 text-[--c-textinput-text] bg-[--c-textinput-background]"
            name="yearmonth-year"
            placeholder="年"
            options={CandidateYearForBirthDay()}
            value={selectedYear}
            onChange={handleYearChange}
            ariaLabel={tUserEditDelete('TU006_038')}
            autoComplete="bday-year"
            id={id}
          />
          <SelectBox
            className="w-18 text-[--c-textinput-text] bg-[--c-textinput-background]"
            name="yearmonth-month"
            placeholder="月"
            options={CandidateMonthForBirthDay()}
            value={selectedMonth}
            onChange={handleMonthChange}
            ariaLabel={tUserEditDelete('TU006_039')}
            autoComplete="bday-month"
            id={id}
          />
        </>
      )}
    </div>
  );
};

export const DateOnlyPicker: React.FC<{
  value?: Date;
  id?: string;
  onChange: (day: Date) => void;
}> = ({ value, id, onChange }) => {
  const { t: tUserEditDelete } = useTranslationWithLang('userEditDelete');
  const { currentLang } = useContext(AppContext);

  const dateValue = value instanceof Date ? value : new Date();

  const handleDateChange = (day: string) => {
    const newDate = new Date(dateValue);
    newDate.setDate(parseInt(day));
    onChange(newDate);
  };

  return (
    <div className="flex gap-2 items-center">
      {currentLang === 'en' ? (
        <>
          <SelectBox
            className="w-18 text-[--c-textinput-text] bg-[--c-textinput-background]"
            name="birthday-day"
            placeholder="day"
            options={CandidateDayForBirthDay(
              dateValue.getFullYear(),
              (dateValue.getMonth() ?? 0) + 1
            )}
            value={dateValue.getDate().toString() ?? ''}
            onChange={handleDateChange}
            ariaLabel={tUserEditDelete('TU006_040')}
            autoComplete="bday-day"
            id={id}
          />
          <span className="text-[--c-textinput-text]">
            {(dateValue.getMonth() ?? 0) + 1}/{dateValue.getFullYear() ?? ''}
          </span>
        </>
      ) : (
        <>
          <span className="text-[--c-textinput-text]">
            {dateValue.getFullYear() ?? ''}/{(dateValue.getMonth() ?? 0) + 1}/
          </span>
          <SelectBox
            className="w-18 text-[--c-textinput-text] bg-[--c-textinput-background]"
            name="birthday-day"
            placeholder="日"
            options={CandidateDayForBirthDay(
              dateValue.getFullYear(),
              (dateValue.getMonth() ?? 0) + 1
            )}
            value={dateValue.getDate().toString() ?? ''}
            onChange={handleDateChange}
            ariaLabel={tUserEditDelete('TU006_040')}
            autoComplete="bday-day"
            id={id}
          />
        </>
      )}
    </div>
  );
};
